import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { BellRing, Bot, Inbox, PenSquare, Settings } from 'lucide-react';
import { useRing } from '../../lib/ring';
import { useInboxUnread } from '../../hooks/useMailboxes';
import { spring } from '../ui/motion';

const MAIL_PATHS = /^\/(inbox|needs-reply|flagged|drafts|sent|folder)(\/|$)/;

function Tab({ to, icon, label, count, tone, pulse, match }: { to: string; icon: React.ReactNode; label: string; count?: number; tone?: 'danger'; pulse?: boolean; match?: RegExp }) {
  const { pathname } = useLocation();
  const active = match ? match.test(pathname) : pathname.startsWith(to);
  return (
    <NavLink to={to} aria-current={active ? 'page' : undefined} className={clsx('relative flex min-w-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl py-1.5 text-[0.625rem] font-semibold', active ? 'text-primary-ink' : 'text-muted')}>
      {active && <motion.span layoutId="mobile-tab" transition={spring} className="absolute inset-0 rounded-2xl bg-primary-soft" />}
      <span className={clsx('relative', pulse && 'rounded-full ring-pulse')}>
        {icon}
        {!!count && <span className={clsx('absolute -right-2.5 -top-1.5 min-w-[16px] rounded-full px-1 text-center text-[0.5625rem] font-bold leading-4 text-white', tone === 'danger' ? 'bg-danger' : 'bg-primary')}>{count > 99 ? '99+' : count}</span>}
      </span>
      <span className="relative">{label}</span>
    </NavLink>
  );
}

/** Floating bottom dock on phones. */
export function MobileNav() {
  const ring = useRing();
  const navigate = useNavigate();
  const unread = useInboxUnread();
  return (
    <nav aria-label="Primary" className="fixed inset-x-3 bottom-[max(10px,env(safe-area-inset-bottom))] z-40 flex items-center gap-0.5 rounded-[26px] border border-hairline bg-surface/90 p-1.5 shadow-float backdrop-blur-xl">
      <Tab to="/inbox" match={MAIL_PATHS} icon={<Inbox className="h-5 w-5" />} label="Mail" count={unread} />
      <Tab to="/review" icon={<Bot className="h-5 w-5" />} label="Review" />
      <button type="button" onClick={() => navigate('/compose')} aria-label="Compose" className="mx-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-primary active:scale-95">
        <PenSquare className="h-5 w-5" />
      </button>
      <Tab to="/reminders" icon={<BellRing className="h-5 w-5" />} label="Reminders" count={ring.pendingCount} tone="danger" pulse={ring.ringingNow} />
      <Tab to="/settings" icon={<Settings className="h-5 w-5" />} label="Settings" />
    </nav>
  );
}
